import * as R from 'ramda'
import { findById as findCountryById } from '../js/countries'
import { findById as findStateById } from '../js/states'
import { auth } from '../firebase'

// The server uses kotlinx encode-defaults=false, so default-valued fields
// (false / 0 / "" / []) and nulls are omitted from admin responses. Fill them
// back in on ingestion so the rest of the admin code sees a complete course.
const ADMIN_COURSE_DEFAULTS = {
  id: 0,
  originalId: 0,
  groupId: 0,
  created: 0,
  updated: 0,
  active: false,
  priv: false,
  name: '',
  info: null,
  info2: null,
  location: null,
  country: 0,
  state: 0,
  holeCount: 0,
  par: 0,
  holes: [],
}

// CURATION_LOAD_NAMES_SUCCESS -> 'names'. One set of cases for four near-identical
// loads, rather than four copies that can drift apart.
const curationSlice = (type) =>
  type.replace('CURATION_LOAD_', '').split('_')[0].toLowerCase()

// Extracts a displayable message from a redux-axios-middleware failure. Not curation-specific:
// any slice that stores an `error` string uses it.
const requestError = (action) =>
  action.error?.response?.data || action.error?.message || 'Request failed'

const initialState = {
  auth: {
    show: false,
    user: null,
    idToken: null,
    uuid: null,
    // Set true after the first Firebase auth state callback so route guards
    // can distinguish "not signed in" from "auth not resolved yet".
    resolved: false,
  },
  user: { data: null, error: null },
  distanceFormat: 'meter',
  search: {
    results: null,
    query: null,
  },
  groups: { data: [], error: null, loading: false, loadingGroup: false },
  course: { data: null, error: null },
  // Community-editable course metadata, keyed by course (group) id.
  metadata: {},
  admin: {
    courses: [],
    groupedCourses: [],
    uniqeCoursesByName: [],
    isLoadingCourses: false,
  },
  // Course curation (/admin/curation). Holds the proposals the server computed and
  // the report from the last apply — nothing else. The decisions being made about
  // those proposals live in component state and localStorage, not here: a country
  // can carry a thousand rows, and running every keystroke through the root
  // reducer would be unusable.
  curation: {
    countries: { data: [], loading: false, error: null },
    country: null,
    private: { data: null, loading: false, error: null },
    names: { data: null, loading: false, error: null },
    hides: { data: null, loading: false, error: null },
    merges: { data: null, loading: false, error: null },
    layouts: { data: null, loading: false, error: null },
    layoutmerges: { data: null, loading: false, error: null },
    phantoms: { data: null, loading: false, error: null },
    // The last apply report, dry run or not. Read report.dryRun to tell which.
    report: null,
    applying: false,
  },
  // The edit monitor (/admin/edits): what signed-in users have changed in public course
  // data, who has been editing, and the last revert report. Like curation, only the server's
  // answers live here — the page's own filters are component state.
  adminEdits: {
    edits: [],
    editors: [],
    loading: false,
    error: null,
    // The last revert report, dry run or not. Read report.dryRun to tell which.
    report: null,
    // True while a revert or a block is in flight, so the page can disable both.
    working: false,
  },
  // The signed-in user's disc inventory (from GET /v3/disc), loaded on demand.
  discs: {
    discs: [],
    bags: [],
    discsToBags: [],
    loading: false,
    loaded: false,
    error: null,
  },
  // Public disc-brand catalog (from GET /v3/disc/brands), loaded on demand.
  brands: { data: [], loading: false, loaded: false, error: null },
}

const reducer = function (state = initialState, action) {
  switch (action.type) {
    case 'LOGIN':
      return { ...state, auth: { ...state.auth, show: true } }

    case 'AUTH_STATE_CHANGED': {
      const { user, idToken } = action
      const isLoggedIn = user && user.emailVerified && user.email
      if (isLoggedIn) {
        return {
          ...state,
          auth: {
            ...state.auth,
            show: false,
            user,
            idToken,
            resolved: true,
          },
        }
      } else {
        return {
          ...state,
          auth: {
            show: false,
            user: null,
            idToken: null,
            uuid: null,
            resolved: true,
          },
          user: { data: null, error: null },
        }
      }
    }

    case 'LOAD_USER_DATA_SUCCESS':
      return {
        ...state,
        user: {
          ...state.user,
          data: JSON.parse(action.payload.data),
          error: null,
        },
      }

    case 'LOAD_USER_DATA_FAIL':
      return {
        ...state,
        user: { ...state.user, data: null, error: action.payload },
      }

    case 'GET_ACCOUNT_UUID_SUCCESS':
      return {
        ...state,
        auth: {
          ...state.auth,
          uuid: JSON.parse(action.payload.data).uuid,
        },
      }

    case 'LOAD_DISCS':
      return { ...state, discs: { ...state.discs, loading: true } }

    case 'LOAD_DISCS_SUCCESS': {
      const { discs, bags, discsToBags } = JSON.parse(action.payload.data)
      return {
        ...state,
        discs: {
          ...state.discs,
          discs: discs || [],
          bags: bags || [],
          discsToBags: discsToBags || [],
          loading: false,
          loaded: true,
          error: null,
        },
      }
    }

    case 'LOAD_DISCS_FAIL':
      return {
        ...state,
        discs: { ...state.discs, loading: false, error: action.payload },
      }

    case 'LOAD_BRANDS':
      return { ...state, brands: { ...state.brands, loading: true } }

    case 'LOAD_BRANDS_SUCCESS': {
      const { brands } = JSON.parse(action.payload.data)
      return {
        ...state,
        brands: {
          ...state.brands,
          data: brands || [],
          loading: false,
          loaded: true,
          error: null,
        },
      }
    }

    case 'LOAD_BRANDS_FAIL':
      return {
        ...state,
        brands: { ...state.brands, loading: false, error: action.payload },
      }

    case 'LOGOUT': {
      auth.signOut()
      return { ...state, auth: { ...state.auth, show: false, user: null } }
    }

    case 'CANCEL_LOGIN':
      return { ...state, auth: { ...state.auth, show: false } }

    case 'LOAD_COURSE_GROUPS':
      return { ...state, groups: { ...state.groups, loading: true } }

    case 'LOAD_COURSE_GROUPS_SUCCESS': {
      return {
        ...state,
        groups: {
          ...state.groups,
          data: JSON.parse(action.payload.data).map((group) => ({
            ...group,
            holes: group.holeCount.join(', '),
            location: group.location
              ? {
                  lat: group.location.lat * 0.000001,
                  lng: group.location.lng * 0.000001,
                }
              : null,
          })),
          loading: false,
          error: null,
        },
      }
    }

    case 'LOAD_COURSE_GROUPS_FAIL':
      return {
        ...state,
        groups: { ...state.groups, loading: false, error: action.payload },
      }

    case 'LOAD_COURSE_GROUP':
      return { ...state, groups: { ...state.groups, loadingGroup: action.id } }

    case 'LOAD_COURSE_GROUP_SUCCESS':
      const groupId = action.meta.previousAction.id
      const courses = JSON.parse(action.payload.data)
      const data = state.groups.data.map(
        (group) => (group.id == groupId ? { ...group, courses } : group) // eslint-disable-line eqeqeq
      )
      return {
        ...state,
        groups: {
          ...state.groups,
          data,
          loadingGroup: false,
          error: null,
        },
        test: data,
      }

    case 'LOAD_COURSE_GROUP_FAIL':
      return {
        ...state,
        groups: { ...state.groups, loadingGroup: false, error: action.payload },
      }

    case 'LOAD_COURSE_SUCCESS':
      return {
        ...state,
        course: {
          ...state.course,
          data: JSON.parse(action.payload.data),
          error: null,
        },
      }

    case 'LOAD_COURSE_FAIL':
      return {
        ...state,
        course: { ...state.course, data: null, error: action.payload },
      }

    case 'LOAD_COURSE_METADATA':
      return {
        ...state,
        metadata: {
          ...state.metadata,
          [action.id]: {
            ...state.metadata[action.id],
            loading: true,
            error: null,
          },
        },
      }

    case 'LOAD_COURSE_METADATA_SUCCESS': {
      const metadataId = action.meta.previousAction.id
      return {
        ...state,
        metadata: {
          ...state.metadata,
          [metadataId]: {
            data: JSON.parse(action.payload.data),
            loading: false,
            error: null,
            notFound: false,
          },
        },
      }
    }

    case 'LOAD_COURSE_METADATA_FAIL': {
      const metadataId = action.meta.previousAction.id
      // A course with no details yet is a 404 — not an error worth surfacing, just "nothing to show".
      const notFound = action.error?.response?.status === 404
      return {
        ...state,
        metadata: {
          ...state.metadata,
          [metadataId]: {
            data: null,
            loading: false,
            error: notFound ? null : action.error,
            notFound,
          },
        },
      }
    }

    case 'SEARCH_COURSES': {
      const query = action.query.toLowerCase()
      if (query.length < 2) return state

      const groups = state.groups.data
      const results = groups.filter(
        (group) => group.name.toLowerCase().indexOf(query) !== -1
      )
      console.log('SEARCH_COURSES', query, groups, results)
      return {
        ...state,
        search: {
          ...state.search,
          results,
          query: action.query,
        },
      }
    }

    case 'CLEAR_SEARCH_RESULTS':
      return {
        ...state,
        search: { ...state.search, results: null, query: null },
      }

    case 'TOGGLE_DISTANCE_FORMAT': {
      const formats = ['meter', 'feet', 'yard']
      const currentIndex = formats.indexOf(state.distanceFormat)
      const distanceFormat = formats[(currentIndex + 1) % formats.length]

      return { ...state, distanceFormat }
    }

    case 'ADMIN_LOAD_ALL_COURSES': {
      return {
        ...state,
        admin: {
          ...state.admin,
          isLoadingCourses: true,
        },
      }
    }

    case 'ADMIN_LOAD_ALL_COURSES_FAIL': {
      return {
        ...state,
        admin: {
          ...state.admin,
          isLoadingCourses: false,
        },
      }
    }

    case 'ADMIN_LOAD_ALL_COURSES_SUCCESS': {
      let courses = JSON.parse(action.payload.data).map((raw) => {
        const course = { ...ADMIN_COURSE_DEFAULTS, ...raw }
        return {
          ...course,
          countryStateId:
            course.country === 236
              ? course.country * 1000 + course.state
              : course.country * 1000,
          country: findCountryById(course.country),
          state: findStateById(course.state),
        }
      })

      // Locally created groups
      // let coursesMap = courses.reduce((map, obj) => {
      //   map[obj.id] = obj
      //   return map
      // }, {})

      // const findParent = (course) => {
      //   if (course.originalId === 0) return course
      //   if (course.originalId === course.id) {
      //     return course
      //   }
      //   const parentCourse = coursesMap[course.originalId]
      //   return findParent(parentCourse)
      // }

      // function groupBy(list, keyGetter) {
      //   const map = new Map()
      //   list.forEach((item) => {
      //     const key = keyGetter(item)
      //     const collection = map.get(key)
      //     if (!collection) {
      //       map.set(key, [{ ...item, groupId: key }])
      //     } else {
      //       collection.push({ ...item, groupId: key })
      //     }
      //   })
      //   return map
      // }

      // const groupedCoursesMap = groupBy(courses, (course) => {
      //   const rootCourse = findParent(course)
      //   return rootCourse.id
      // })

      // // const groupedCourses = Array.from(groupedCoursesMap.values())
      // const groupedCourses = new Array()
      // for (let [id, courses] of groupedCoursesMap) {
      //   const filteredCourses = courses.filter((c) => c.active && !c.priv)
      //   if (filteredCourses.length > 0) {
      //     groupedCourses.push(filteredCourses)
      //   }
      // }

      // courses = groupedCourses.flatMap((group) => group.map((course) => course))
      const blahCourses = Object.values(
        R.groupBy((c) => c.name, courses)
      ).filter((group) => {
        if (group.length < 2) return false
        const countryId = parseInt(group[0].countryStateId / 1000)
        return group
          .slice(1)
          .map((g) => parseInt(g.countryStateId / 1000))
          .some((csi) => csi === countryId + 1 || csi === countryId - 1)
      })
      console.log(
        'ADMIN blahCourses',
        blahCourses,
        blahCourses.map((group) => group[0].name)
      )

      courses = courses.filter((c) => c.active && !c.priv && c.groupId !== 0)
      const groupedCourses = Object.values(
        R.groupBy((course) => course.groupId, courses)
      )

      const uniqCourseIdentifier = (course) =>
        course.name.toLowerCase().trim() + course.countryStateId

      console.log('ADMIN courses', courses)
      console.log('ADMIN groupedCourses', groupedCourses)

      const problemGroups = groupedCourses.filter((group) => {
        if (group.length < 2) return false
        const firstGroupName = group[0].name
          .replaceAll(/[,.]/gi, ' ')
          .replaceAll(/\s\s/g, ' ')
          .split(' ')[0]
          .toLowerCase()
        return group
          .slice(1)
          .map((g) =>
            g.name
              .replaceAll(/[,.]/gi, ' ')
              .replaceAll(/\s\s/g, ' ')
              .split(' ')[0]
              .toLowerCase()
          )
          .some((name) => name !== firstGroupName)
      })
      console.log(
        'ADMIN problemGroups',
        problemGroups,
        problemGroups.map((group) => group.map((g) => g.name))
      )

      return {
        ...state,
        admin: {
          ...state.admin,
          isLoadingCourses: false,
          courses,
          groupedCourses,
          uniqeCoursesByName: R.uniqBy(
            uniqCourseIdentifier,
            R.reverse(R.filter((course) => course.active, courses))
          ),
        },
      }
    }

    case 'ADMIN_TOGGLE_COURSE_STATUS_SUCCESS': {
      const {
        id,
        active = false,
        priv = false,
        updated,
      } = JSON.parse(action.payload.data)

      const courses = state.admin.courses.map((course) =>
        course.id === id
          ? {
              ...course,
              active,
              priv,
              updated,
            }
          : course
      )

      const uniqCourseIdentifier = (course) =>
        course.name.toLowerCase().trim() + course.countryStateId

      return {
        ...state,
        admin: {
          ...state.admin,
          courses,
          uniqeCoursesByName: R.uniqBy(
            uniqCourseIdentifier,
            R.reverse(R.filter((course) => course.active, courses))
          ),
        },
      }
    }

    case 'ADMIN_EDITS_LOAD':
    case 'ADMIN_EDITS_LOAD_EDITORS': {
      return { ...state, adminEdits: { ...state.adminEdits, loading: true, error: null } }
    }

    case 'ADMIN_EDITS_LOAD_SUCCESS': {
      return {
        ...state,
        adminEdits: {
          ...state.adminEdits,
          edits: JSON.parse(action.payload.data),
          loading: false,
          error: null,
        },
      }
    }

    case 'ADMIN_EDITS_LOAD_EDITORS_SUCCESS': {
      return {
        ...state,
        adminEdits: {
          ...state.adminEdits,
          editors: JSON.parse(action.payload.data),
          loading: false,
          error: null,
        },
      }
    }

    case 'ADMIN_EDITS_LOAD_FAIL':
    case 'ADMIN_EDITS_LOAD_EDITORS_FAIL': {
      return {
        ...state,
        adminEdits: { ...state.adminEdits, loading: false, error: requestError(action) },
      }
    }

    // A dry run and a real revert are the same request with a different flag, so they land
    // here together; the page reads report.dryRun to tell them apart.
    case 'ADMIN_EDITS_REVERT':
    case 'ADMIN_EDITS_BLOCK': {
      return { ...state, adminEdits: { ...state.adminEdits, working: true, error: null } }
    }

    case 'ADMIN_EDITS_REVERT_SUCCESS': {
      return {
        ...state,
        adminEdits: {
          ...state.adminEdits,
          report: JSON.parse(action.payload.data),
          working: false,
        },
      }
    }

    case 'ADMIN_EDITS_BLOCK_SUCCESS': {
      return { ...state, adminEdits: { ...state.adminEdits, working: false } }
    }

    case 'ADMIN_EDITS_REVERT_FAIL':
    case 'ADMIN_EDITS_BLOCK_FAIL': {
      return {
        ...state,
        adminEdits: { ...state.adminEdits, working: false, error: requestError(action) },
      }
    }

    case 'CURATION_SET_COUNTRY': {
      return {
        ...state,
        curation: { ...state.curation, country: action.payload, report: null },
      }
    }

    // The layouts pass submits to two endpoints, so the report in the store is only
    // the last one's. The component adds them up and puts the sum back here.
    case 'CURATION_SET_REPORT': {
      return { ...state, curation: { ...state.curation, report: action.payload } }
    }

    case 'CURATION_CLEAR_REPORT': {
      return { ...state, curation: { ...state.curation, report: null } }
    }

    case 'CURATION_LOAD_COUNTRIES':
    case 'CURATION_LOAD_PRIVATE':
    case 'CURATION_LOAD_NAMES':
    case 'CURATION_LOAD_HIDES':
    case 'CURATION_LOAD_MERGES':
    case 'CURATION_LOAD_LAYOUTS':
    case 'CURATION_LOAD_LAYOUTMERGES':
    case 'CURATION_LOAD_PHANTOMS': {
      return {
        ...state,
        curation: {
          ...state.curation,
          [curationSlice(action.type)]: {
            ...state.curation[curationSlice(action.type)],
            loading: true,
            error: null,
          },
        },
      }
    }

    case 'CURATION_LOAD_COUNTRIES_SUCCESS':
    case 'CURATION_LOAD_PRIVATE_SUCCESS':
    case 'CURATION_LOAD_NAMES_SUCCESS':
    case 'CURATION_LOAD_HIDES_SUCCESS':
    case 'CURATION_LOAD_MERGES_SUCCESS':
    case 'CURATION_LOAD_LAYOUTS_SUCCESS':
    case 'CURATION_LOAD_LAYOUTMERGES_SUCCESS':
    case 'CURATION_LOAD_PHANTOMS_SUCCESS': {
      return {
        ...state,
        curation: {
          ...state.curation,
          [curationSlice(action.type)]: {
            data: JSON.parse(action.payload.data),
            loading: false,
            error: null,
          },
        },
      }
    }

    case 'CURATION_LOAD_COUNTRIES_FAIL':
    case 'CURATION_LOAD_PRIVATE_FAIL':
    case 'CURATION_LOAD_NAMES_FAIL':
    case 'CURATION_LOAD_HIDES_FAIL':
    case 'CURATION_LOAD_MERGES_FAIL':
    case 'CURATION_LOAD_LAYOUTS_FAIL':
    case 'CURATION_LOAD_LAYOUTMERGES_FAIL':
    case 'CURATION_LOAD_PHANTOMS_FAIL': {
      return {
        ...state,
        curation: {
          ...state.curation,
          [curationSlice(action.type)]: {
            ...state.curation[curationSlice(action.type)],
            loading: false,
            error: requestError(action),
          },
        },
      }
    }

    // A dry run and a real apply are the same action with a different body, so they
    // land here together. The component reads report.dryRun to tell them apart.
    case 'CURATION_APPLY_PRIVATE':
    case 'CURATION_APPLY_NAMES':
    case 'CURATION_APPLY_HIDES':
    case 'CURATION_APPLY_MERGES':
    case 'CURATION_APPLY_LAYOUTS':
    case 'CURATION_APPLY_LAYOUTMERGES': {
      return { ...state, curation: { ...state.curation, applying: true } }
    }

    case 'CURATION_APPLY_PRIVATE_SUCCESS':
    case 'CURATION_APPLY_NAMES_SUCCESS':
    case 'CURATION_APPLY_HIDES_SUCCESS':
    case 'CURATION_APPLY_MERGES_SUCCESS':
    case 'CURATION_APPLY_LAYOUTS_SUCCESS':
    case 'CURATION_APPLY_LAYOUTMERGES_SUCCESS': {
      return {
        ...state,
        curation: {
          ...state.curation,
          applying: false,
          report: JSON.parse(action.payload.data),
        },
      }
    }

    case 'CURATION_APPLY_PRIVATE_FAIL':
    case 'CURATION_APPLY_NAMES_FAIL':
    case 'CURATION_APPLY_HIDES_FAIL':
    case 'CURATION_APPLY_MERGES_FAIL':
    case 'CURATION_APPLY_LAYOUTS_FAIL':
    case 'CURATION_APPLY_LAYOUTMERGES_FAIL': {
      return {
        ...state,
        curation: {
          ...state.curation,
          applying: false,
          report: { failed: true, error: requestError(action) },
        },
      }
    }

    case 'ADMIN_RENAME_GROUP_SUCCESS': {
      console.log('Course group has been renamed, reload groups now')
      return state
    }

    case 'ADMIN_MERGE_GROUPS_SUCCESS': {
      console.log('Courses have been merged, reload groups now')
      return state
    }

    default:
      return state
  }
}

export default reducer
